import React, { useState, useEffect } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';
import { timetableApi } from '../services/api';
import toast from 'react-hot-toast';

interface ConstraintData {
  class_code: string;
  class_class_code: string;
  subject_code: string;
  teacher: string;
  valid_slot_count: number;
  periods_needed: number;
  ratio: number;
  valid_slots: { day: number; period: number }[];
}

interface ConstraintsAnalysisResponse {
  constraints: ConstraintData[];
  total_classes: number;
}

const ConstraintsAnalysisPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [constraintsData, setConstraintsData] = useState<ConstraintsAnalysisResponse | null>(null);
  const [expandedClasses, setExpandedClasses] = useState<Set<string>>(new Set());
  const [selectedGrades, setSelectedGrades] = useState<number[]>([8, 9]);

  const loadConstraints = async () => {
    setLoading(true);
    try {
      const response = await timetableApi.getConstraintsAnalysis(selectedGrades);
      setConstraintsData(response.data);
    } catch (error) {
      console.error('Error loading constraints:', error);
      toast.error('Failed to load constraints analysis');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConstraints();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleClass = (classCode: string) => {
    const newExpanded = new Set(expandedClasses);
    if (newExpanded.has(classCode)) {
      newExpanded.delete(classCode);
    } else {
      newExpanded.add(classCode);
    }
    setExpandedClasses(newExpanded);
  };

  const toggleGrade = (grade: number) => {
    if (selectedGrades.includes(grade)) {
      if (selectedGrades.length > 1) {
        setSelectedGrades(selectedGrades.filter(g => g !== grade));
      }
    } else {
      setSelectedGrades([...selectedGrades, grade].sort());
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold mb-6" style={{ color: '#112658' }}>
        Constraints Analysis
      </h1>

      {/* Grade Selection */}
      <div className="mb-6 flex items-center gap-4">
        <span className="font-semibold">Select Grades:</span>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={selectedGrades.includes(8)}
            onChange={() => toggleGrade(8)}
            className="w-4 h-4"
          />
          <span>Grade 8</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={selectedGrades.includes(9)}
            onChange={() => toggleGrade(9)}
            className="w-4 h-4"
          />
          <span>Grade 9</span>
        </label>
        <button
          onClick={loadConstraints}
          disabled={loading}
          className="px-4 py-2 rounded text-white font-semibold disabled:opacity-50"
          style={{ backgroundColor: '#112658' }}
        >
          {loading ? 'Loading...' : 'Refresh Analysis'}
        </button>
      </div>

      {loading && !constraintsData ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#112658' }}></div>
          <p className="mt-4 text-gray-600">Analyzing constraints...</p>
        </div>
      ) : constraintsData ? (
        <>
          {/* Summary */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h2 className="text-xl font-semibold mb-2">Summary</h2>
            <p className="text-gray-700">
              Total Classes: <strong>{constraintsData.total_classes}</strong>
            </p>
            <p className="text-sm text-gray-600 mt-1">
              Lower ratios indicate more constrained classes (fewer available slots per period needed)
            </p>
          </div>

          {/* Constraints Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead style={{ backgroundColor: '#112658' }}>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Class
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Subject
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Teacher
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-white uppercase tracking-wider">
                      Valid Slots
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-white uppercase tracking-wider">
                      Periods Needed
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-white uppercase tracking-wider">
                      Ratio
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {constraintsData.constraints
                    .sort((a, b) => {
                      const ratioA = a.valid_slot_count / a.periods_needed;
                      const ratioB = b.valid_slot_count / b.periods_needed;
                      return ratioA - ratioB; // Smallest ratio first (most constrained)
                    })
                    .map((item, index) => (
                    <React.Fragment key={item.class_code}>
                      <tr className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                          {item.class_code}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                          {item.subject_code}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                          {item.teacher}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-700">
                          {item.valid_slot_count}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-700">
                          {item.periods_needed}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-semibold">
                          <span className={
                            (item.valid_slot_count / item.periods_needed) < 2 ? 'text-red-600' :
                            (item.valid_slot_count / item.periods_needed) < 4 ? 'text-orange-600' :
                            'text-green-600'
                          }>
                            {(item.valid_slot_count / item.periods_needed).toFixed(2)}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <button
                            onClick={() => toggleClass(item.class_code)}
                            className="inline-flex items-center gap-1 text-sm font-medium hover:underline"
                            style={{ color: '#112658' }}
                          >
                            {expandedClasses.has(item.class_code) ? (
                              <>
                                Hide <ChevronUpIcon className="h-4 w-4" />
                              </>
                            ) : (
                              <>
                                Show <ChevronDownIcon className="h-4 w-4" />
                              </>
                            )}
                          </button>
                        </td>
                      </tr>
                      {expandedClasses.has(item.class_code) && (
                        <tr>
                          <td colSpan={7} className="px-4 py-4 bg-gray-100">
                            <div className="space-y-2">
                              <h4 className="font-semibold text-sm text-gray-700">
                                Available Slots for {item.class_code}:
                              </h4>
                              {item.valid_slots.length > 0 ? (
                                <div className="grid grid-cols-6 gap-2">
                                  {item.valid_slots.map((slot, idx) => (
                                    <div
                                      key={idx}
                                      className="px-2 py-1 bg-white border border-gray-300 rounded text-xs text-center"
                                    >
                                      Day {slot.day}, P{slot.period}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-red-600">No valid slots available</p>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center py-12 text-gray-500">
          No data available. Click "Refresh Analysis" to load.
        </div>
      )}
    </div>
  );
};

export default ConstraintsAnalysisPage;
